/* eslint-disable react/jsx-pascal-case */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  useMaterialReactTable,
  type MRT_TableOptions,
} from 'material-react-table';
import {
  CloudUpload,
  Delete,
  Edit,
  Visibility,
  VisibilityOff,
  FolderOpen,
  Sync,
  Refresh,
} from '@mui/icons-material';
import { SupportMaterialFile, FolderData } from './types';
import { SupportMaterialService } from './supportMaterialService';
import { UploadModal } from './UploadModal';
import { EditFileModal } from './EditFileModal';
import NavBar from "../../components/navbar/navbar"; 

const SupportMaterialDoc: React.FC = () => {
  // ==================== STATE ====================
  const [materials, setMaterials] = useState<FolderData[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  
  // Modals
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SupportMaterialFile | null>(null);
  
  // Notifications
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // ==================== COMPUTED ====================
  
  // Aplanar todos los archivos de todas las carpetas para la tabla
  const allFiles = useMemo(() => {
    console.log('[Admin] Calculando allFiles. selectedFolder:', selectedFolder);
    
    if (selectedFolder) {
      const folderData = materials.find(m => m.folder === selectedFolder);
      const files = folderData?.files || [];
      console.log('[Admin] Archivos filtrados por carpeta', selectedFolder, ':', files.length);
      return files;
    }
    
    const files = materials.flatMap(m => m.files);
    console.log('[Admin] Todos los archivos:', files.length);
    return files;
  }, [materials, selectedFolder]);

  // ==================== LOAD DATA ====================

  const loadMaterials = useCallback(async () => {
    console.log('[Admin] Cargando materiales...');
    setLoading(true);
    try {
      const data = await SupportMaterialService.listAllByFolders();
      setMaterials(data);
      console.log('[Admin] ✓ Materiales cargados:', data.length, 'carpetas');
    } catch (error: any) {
      console.error('[Admin] Error cargando materiales:', error);
      showNotification(error.message || 'Error al cargar materiales', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFolders = useCallback(async () => {
    console.log('[Admin] Cargando lista de carpetas...');
    try {
      const folderList = await SupportMaterialService.getFolders();
      setFolders(folderList);
      console.log('[Admin] ✓ Carpetas cargadas:', folderList.length);
    } catch (error: any) {
      console.error('[Admin] Error loading folders:', error);
    }
  }, []);

  useEffect(() => {
    console.log('[Admin] Montando componente, cargando datos iniciales');
    loadMaterials();
    loadFolders();
  }, [loadMaterials, loadFolders]);

  // ==================== HANDLERS ====================

  const handleToggleStatus = async (file: SupportMaterialFile) => {
    console.log('[Admin] Toggling status de archivo:', file.id, file.fileName);
    try {
      const username = localStorage.getItem('username') || undefined;
      await SupportMaterialService.toggleFileStatus(file.id, !file.active, username);
      showNotification(
        `Archivo ${!file.active ? 'activado' : 'desactivado'} exitosamente`,
        'success'
      );
      await loadMaterials();
    } catch (error: any) {
      showNotification(error.message || 'Error al cambiar estado', 'error');
    }
  };

  const handleDeleteFile = async (file: SupportMaterialFile) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${file.fileName}"?`)) {
      return;
    }

    console.log('[Admin] Eliminando archivo:', file.id, file.fileName);
    try {
      const username = localStorage.getItem('username') || undefined;
      await SupportMaterialService.deleteFile(file.id, username);
      showNotification('Archivo eliminado exitosamente', 'success');
      await loadMaterials();
    } catch (error: any) {
      showNotification(error.message || 'Error al eliminar archivo', 'error');
    }
  };

  const handleEditFile = (file: SupportMaterialFile) => {
    console.log('[Admin] Editando archivo:', file.id, file.fileName);
    setSelectedFile(file);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    await loadMaterials();
    setEditModalOpen(false);
    setSelectedFile(null);
  };

  const handleSyncS3 = async () => {
    console.log('[Admin] Sincronizando con S3...');
    try {
      const result = await SupportMaterialService.syncWithS3();
      
      let message = `Sincronización completada:\n`;
      message += `BD: ${result.totalInDB} archivos\n`;
      message += `S3: ${result.totalInS3} archivos\n`;
      
      if (result.orphanedInDB.length > 0) {
        message += `\nArchivos huérfanos en BD: ${result.orphanedInDB.length}`;
      }
      
      if (result.orphanedInS3.length > 0) {
        message += `\nArchivos huérfanos en S3: ${result.orphanedInS3.length}`;
      }
      
      alert(message);
      await loadMaterials();
    } catch (error: any) {
      showNotification(error.message || 'Error al sincronizar', 'error');
    }
  };

  const handleDeleteMultiple = async (rows: MRT_Row<SupportMaterialFile>[]) => {
    const ids = rows.map(row => row.original.id);
    
    if (!window.confirm(`¿Estás seguro de eliminar ${ids.length} archivos?`)) {
      return;
    }

    console.log('[Admin] Eliminando múltiples archivos:', ids.length);
    try {
      const username = localStorage.getItem('username') || undefined;
      await SupportMaterialService.deleteMultipleFiles({ ids, deletedBy: username });
      showNotification(`${ids.length} archivos eliminados exitosamente`, 'success');
      await loadMaterials();
    } catch (error: any) {
      showNotification(error.message || 'Error al eliminar archivos', 'error');
    }
  };

  const handleRefresh = () => {
    console.log('[Admin] Refrescando datos...');
    loadMaterials();
    loadFolders();
  };

  // ==================== TABLE COLUMNS ====================

  const columns = useMemo<MRT_ColumnDef<SupportMaterialFile>[]>(
    () => [
      {
        accessorKey: 'fileName',
        header: 'Nombre del Archivo',
        size: 250,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
              {row.original.fileName}
            </Typography>
            {row.original.description && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.original.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'folder',
        header: 'Carpeta',
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            icon={<FolderOpen />}
            label={cell.getValue<string>()}
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          />
        ),
      },
      {
        accessorKey: 'fileType',
        header: 'Tipo',
        size: 100,
        Cell: ({ cell }) => {
          const type = cell.getValue<string>();
          const colorMap: Record<string, any> = {
            IMAGE: 'info',
            PDF: 'error',
            DOCUMENT: 'primary',
            VIDEO: 'secondary',
            AUDIO: 'success',
            SPREADSHEET: 'warning',
          };
          return (
            <Chip
              label={type}
              size="small"
              color={colorMap[type] || 'default'}
            />
          );
        },
      },
      {
        accessorKey: 'sizeBytes',
        header: 'Tamaño',
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {formatFileSize(cell.getValue<number>())}
          </Typography>
        ),
      },
      {
        accessorKey: 'orderPosition',
        header: 'Orden',
        size: 80,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {cell.getValue<number>() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'active',
        header: 'Estado',
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={row.original.active ? 'Activo' : 'Inactivo'}
            size="small"
            color={row.original.active ? 'success' : 'default'}
            variant={row.original.active ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        accessorKey: 'existsInS3',
        header: 'S3',
        size: 80,
        Cell: ({ cell }) => {
          const exists = cell.getValue<boolean>();
          return (
            <Chip
              label={exists ? 'Sí' : 'No'}
              size="small"
              color={exists ? 'success' : 'error'}
              variant="outlined"
              sx={{
                fontWeight: exists ? 600 : 400,
              }}
            />
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Creado',
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {new Date(cell.getValue<string>()).toLocaleDateString('es-MX')}
          </Typography>
        ),
      },
      {
        accessorKey: 'createdBy',
        header: 'Creado por',
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
    ],
    []
  );

  // ==================== TABLE CONFIG ====================

  const table = useMaterialReactTable({
    columns,
    data: allFiles,
    enableRowSelection: true,
    enableColumnOrdering: true,
    enableGlobalFilter: true,
    enablePagination: true,
    enableStickyHeader: true,
    enableDensityToggle: true,
    initialState: {
      density: 'compact',
      pagination: { pageSize: 25, pageIndex: 0 },
      sorting: [{ id: 'folder', desc: false }],
    },
    state: {
      isLoading: loading,
    },
    // ==================== ESTILOS MEJORADOS ====================
    muiTableContainerProps: {
      sx: { 
        maxHeight: '600px',
        // Scrollbar personalizado
        '&::-webkit-scrollbar': {
          width: '10px',
          height: '10px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '5px',
          '&:hover': {
            backgroundColor: '#555',
          },
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      },
    },
    muiTableProps: {
      sx: {
        // Fondo blanco para toda la tabla
        backgroundColor: '#ffffff',
        '& .MuiTableCell-root': {
          borderColor: 'rgba(224, 224, 224, 1)', // Bordes más visibles
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        // Headers con fondo gris claro y texto oscuro
        backgroundColor: '#f5f5f5',
        color: '#000000',
        fontWeight: 600,
        fontSize: '0.875rem',
        borderBottom: '2px solid',
        borderBottomColor: 'divider',
        '& .Mui-TableHeadCell-Content': {
          color: '#000000',
        },
        '& .MuiTableSortLabel-root': {
          color: '#000000',
          '&:hover': {
            color: '#1976d2',
          },
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        // Celdas con fondo blanco y texto oscuro
        backgroundColor: '#ffffff',
        color: '#000000',
        fontSize: '0.875rem',
        padding: '12px 16px',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        // Filas con hover sutil
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#f9f9f9 !important',
        },
        // Filas seleccionadas
        '&.Mui-selected': {
          backgroundColor: '#e3f2fd !important',
          '&:hover': {
            backgroundColor: '#bbdefb !important',
          },
        },
        // Resaltar filas inactivas o sin S3
        ...((!row.original.active || !row.original.existsInS3) && {
          opacity: 0.7,
          backgroundColor: '#fff9c4',
          '&:hover': {
            backgroundColor: '#fff59d !important',
          },
        }),
      },
    }),
    muiTopToolbarProps: {
      sx: {
        backgroundColor: '#fafafa',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: '#fafafa',
        borderTop: '1px solid',
        borderTopColor: 'divider',
      },
    },
    // ==================== TOOLBAR ACTIONS ====================
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="secondary" 
          startIcon={<CloudUpload />}
          onClick={() => {
            console.log('[Admin] Abriendo modal de upload');
            setUploadModalOpen(true);
          }}
        >
          Subir Archivos
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<Sync />}
          onClick={handleSyncS3}
        >
          Sincronizar S3
        </Button>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
        >
          Refrescar
        </Button>

        {table.getSelectedRowModel().rows.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => handleDeleteMultiple(table.getSelectedRowModel().rows)}
          >
            Eliminar ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Ver archivo">
          <IconButton
            size="small"
            onClick={() => {
              console.log('[Admin] Abriendo archivo:', row.original.url);
              window.open(row.original.url, '_blank');
            }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Editar">
          <IconButton
            size="small"
            onClick={() => handleEditFile(row.original)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={row.original.active ? 'Desactivar' : 'Activar'}>
          <IconButton
            size="small"
            onClick={() => handleToggleStatus(row.original)}
          >
            {row.original.active ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Eliminar">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteFile(row.original)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  // ==================== RENDER ====================

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: '100vh' }}>
      <NavBar /> 
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Material de Apoyo
        </Typography>
        
        
        {/* Stats */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Chip 
            label={`${allFiles.length} archivos`} 
            color="secondary" 
            size="small"
          />
          <Chip 
            label={`${allFiles.filter(f => f.active).length} activos`} 
            color="secondary" 
            size="small"
          />
          <Chip 
            label={`${allFiles.filter(f => f.existsInS3).length} en S3`} 
            color="info" 
            size="small"
          />
          {allFiles.filter(f => !f.existsInS3).length > 0 && (
            <Chip 
              label={`${allFiles.filter(f => !f.existsInS3).length} faltan en S3`} 
              color="error" 
              size="small"
            />
          )}
        </Stack>
      </Paper>

      {/* Filtro de carpetas */}
      {folders.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Todas las carpetas"
              onClick={() => {
                console.log('[Admin] Filtro: Todas las carpetas');
                setSelectedFolder('');
              }}
              color={selectedFolder === '' ? 'secondary' : 'default'}
              variant={selectedFolder === '' ? 'filled' : 'outlined'}
            />
            {folders.map((folder) => (
              <Chip
                key={folder}
                label={folder}
                onClick={() => {
                  console.log('[Admin] Filtro: Carpeta', folder);
                  setSelectedFolder(folder);
                }}
                color={selectedFolder === folder ? 'primary' : 'default'}
                variant={selectedFolder === folder ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Table */}
      <MaterialReactTable table={table} />

      {/* Upload Modal */}
      <UploadModal
        open={uploadModalOpen}
        onClose={() => {
          console.log('[Admin] Cerrando modal de upload');
          setUploadModalOpen(false);
        }}
        folders={folders}
        onUploadComplete={async () => {
          console.log('[Admin] Upload completado, recargando datos');
          await loadMaterials();
          await loadFolders();
        }}
        onNotify={showNotification}
      />

      {/* Edit Modal */}
      {selectedFile && (
        <EditFileModal
          open={editModalOpen}
          file={selectedFile}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedFile(null);
          }}
          onSave={handleSaveEdit}
          onNotify={showNotification}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={hideNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ==================== HELPERS ====================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default SupportMaterialDoc;
